import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../config';

export type TriageFindingSeverity = 'critical' | 'high' | 'medium' | 'low';

/** Buckets a finding into one of the three fixed sections in the UI. */
export type TriageFindingKind = 'cascade' | 'bug' | 'maintenance';

/** Overall read on the run: is anything on fire? */
export type TriageVerdict = 'all_clear' | 'needs_attention' | 'urgent';

/** Generic run status — shared across run-like entities. */
export type TriageRunStatus =
  | 'pending'
  | 'waiting'
  | 'running'
  | 'completed'
  | 'failed'
  | 'canceled'
  | 'skipped'
  | 'timeout';

export interface TriageFindingRun {
  workflowRunId: string;
  workflowName: string | null;
  errorMessage: string | null;
  isNew: boolean;
  testRunId: string | null;
}

export interface TriageFindingIssue {
  issueId: string;
  shortcode: string;
  title: string;
  status: string;
}

export interface TriageFinding {
  id: string;
  rank: number;
  kind: TriageFindingKind;
  title: string;
  severity: TriageFindingSeverity;
  bodyMd: string;
  failureCount: number;
  newFailureCount: number;
  runs: TriageFindingRun[];
  issues: TriageFindingIssue[];
}

export interface TriageRunResponse {
  id: string;
  status: TriageRunStatus;
  headline: string | null;
  verdict: TriageVerdict | null;
  summaryMd: string | null;
  failureCount: number;
  newFailureCount: number;
  diagnosticsPending: number;
  diagnosticsTotal: number;
  diagnosticsTimedOut: boolean;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
  findings: TriageFinding[];
}

export interface TriageByExecutionResponse {
  ok: true;
  triage: TriageRunResponse | null;
}

/**
 * What the triage panel is showing triage for. A native suite run reads
 * `/triage/by-execution/:id`; an external run reads `/triage/by-external-run/:id`
 * — the server returns the same `TriageRunResponse` shape either way, but a
 * finding's `runs[].testRunId` means a different thing per kind (a native
 * test-run id vs. an external run item id), which is why the caller (not this
 * hook) resolves the finding-run link.
 */
export type TriageSubject = { kind: 'execution'; id: string } | { kind: 'externalRun'; id: string };

const isActive = (status: TriageRunStatus | undefined): boolean =>
  status === 'pending' || status === 'waiting' || status === 'running';

function triagePath(subject: TriageSubject): string {
  return subject.kind === 'execution'
    ? `/triage/by-execution/${subject.id}`
    : `/triage/by-external-run/${subject.id}`;
}

/**
 * Poll a `triage: null` response (no row yet) at this slower interval —
 * production observation (run `fa35bf09`) put the triage row landing ~12
 * minutes after the run finished, and the pipeline's own budget is up to
 * ~20 minutes (10-minute diagnostics deadline + up to a 10-minute agent), so
 * this is a long wait with no reason to poll it as eagerly as an in-progress
 * triage row's status.
 */
const AWAITING_TRIAGE_POLL_MS = 10_000;

export function useTriage(subject: TriageSubject, awaitingTriage: boolean) {
  return useQuery({
    // Kept distinct per kind — the two endpoints are not interchangeable data,
    // just the same response shape, and an id collision across kinds is not a
    // concern worth sharing a cache key over.
    queryKey: ['triage', subject.kind, subject.id],
    enabled: !!subject.id,
    queryFn: async (): Promise<TriageByExecutionResponse> => {
      const response = await fetch(`${API_URL}${triagePath(subject)}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 403) {
          // Feature flag off — treat as no triage
          return { ok: true, triage: null };
        }
        throw new Error('Failed to fetch triage');
      }
      return response.json();
    },
    refetchInterval: (query) => {
      const triage = query.state.data?.triage;
      if (isActive(triage?.status)) return 3000;
      // `triage: null` is "no row yet", not "never coming" — a native run's
      // triage row appears only once diagnostics start, and an external run's
      // starts only once the run seals, so `awaitingTriage` (in progress, or
      // finished recently enough that the pipeline could still be running —
      // see the caller) may still keep growing a triage row with no status
      // change to trigger the branch above. Without this, watching a live
      // run never picks up its triage after it seals until the page reloads.
      if (triage === null && awaitingTriage) return AWAITING_TRIAGE_POLL_MS;
      return false;
    },
  });
}
